/**
 * Chart colour and formatting helpers.
 *
 * Colours resolve from the CSS custom properties in tokens.css, so light and
 * dark mode are handled in one place and adopting app2's palette means editing
 * that stylesheet, not this file.
 */

/** Categorical slots in fixed order. Never cycled — see seriesColor. */
export const SERIES_SLOTS = 8

/**
 * Colour for series index i.
 *
 * <p>Beyond the eight defined slots the colour is deliberately the neutral
 * "other" grey rather than a wrapped-around hue. Reusing slot 1 for the ninth
 * series would make two different things the same colour, which is worse than
 * admitting the chart has run out of distinguishable colours: charts with more
 * than eight series fold the tail into an "Other" bucket instead.
 */
export function seriesColor(index: number): string {
  if (index < 0 || index >= SERIES_SLOTS) return 'var(--series-other)'
  return `var(--series-${index + 1})`
}

/**
 * Fixed status colours. Reserved — never reused for a data series.
 *
 * Two ramps, because the same colour cannot do both jobs on app2's white
 * cards: STATUS_COLOR is chip and label *text* and clears 5:1 on its own
 * tint, while STATUS_FILL is for solid marks — strip cells, the hero glyph,
 * the card's left rule — where a text-grade colour would look muddy.
 */
export const STATUS_COLOR: Record<string, string> = {
  OK: 'var(--status-ok)',
  WARNING: 'var(--status-warning)',
  CRITICAL: 'var(--status-critical)',
  UNKNOWN: 'var(--status-unknown)',
}

export const STATUS_FILL: Record<string, string> = {
  OK: 'var(--status-ok-fill)',
  WARNING: 'var(--status-warning-fill)',
  CRITICAL: 'var(--status-critical-fill)',
  UNKNOWN: 'var(--status-unknown-fill)',
}

export const STATUS_BG: Record<string, string> = {
  OK: 'var(--status-ok-bg)',
  WARNING: 'var(--status-warning-bg)',
  CRITICAL: 'var(--status-critical-bg)',
  UNKNOWN: 'var(--status-unknown-bg)',
}

/**
 * Status is always paired with a glyph and a word.
 * Colour alone is never the carrier of meaning.
 */
export const STATUS_GLYPH: Record<string, string> = {
  OK: '●',        // filled circle
  WARNING: '▲',   // triangle
  CRITICAL: '■',  // square
  UNKNOWN: '○',   // hollow circle
}

export const STATUS_LABEL: Record<string, string> = {
  OK: 'OK',
  WARNING: 'Warning',
  CRITICAL: 'Critical',
  UNKNOWN: 'Unknown',
}

/**
 * Dash patterns, used as a second channel when hues have to repeat.
 *
 * <p>Some charts must show every series — the import providers, where folding
 * the tail into "Other" would hide exactly the provider someone came to check.
 * There is no honest way to draw 18 categorical hues, so past the eight slots
 * the hues cycle and the dash pattern changes with each lap. Colour then names
 * the slot and the dash names the lap, which is a pairing a viewer can read off
 * the legend and which survives colour-vision deficiency.
 */
export const SERIES_DASHES = ['0', '7 3', '2 3', '11 3 2 3']

/** Hue for series i, cycling rather than greying out past the eight slots. */
export function seriesCycleColor(index: number): string {
  const slot = ((index % SERIES_SLOTS) + SERIES_SLOTS) % SERIES_SLOTS
  return `var(--series-${slot + 1})`
}

/** Dash pattern for series i — solid on the first lap of eight. */
export function seriesDash(index: number): string {
  const lap = Math.floor(Math.max(index, 0) / SERIES_SLOTS)
  return SERIES_DASHES[lap % SERIES_DASHES.length]
}

/** Keep the tail of a long series list honest instead of silently dropping it. */
export function foldToOther<T>(
  items: T[],
  weight: (item: T) => number,
  max = SERIES_SLOTS,
): { kept: T[]; otherCount: number } {
  if (items.length <= max) return { kept: items, otherCount: 0 }
  const sorted = [...items].sort((a, b) => weight(b) - weight(a))
  return { kept: sorted.slice(0, max - 1), otherCount: items.length - (max - 1) }
}

// -- value formatting ---------------------------------------------------------

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']

export function formatValue(value: number | null | undefined, unit?: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'

  switch (unit) {
    case 'percent':
      return `${trim(value, value < 10 ? 1 : 0)}%`
    case 'bytes':
      return formatBytes(value)
    case 'kilobytes':
      return formatBytes(value * 1024)
    case 'bytes/s':
      return `${formatBytes(value)}/s`
    case 'kilobytes/s':
      return `${formatBytes(value * 1024)}/s`
    case 'seconds':
      return formatDuration(value)
    case 'milliseconds':
      return value >= 1000 ? formatDuration(value / 1000) : `${trim(value, 0)} ms`
    case 'bool':
      return value >= 1 ? 'yes' : 'no'
    case 'count/s':
    case 'per_second':
      return `${trim(value, value < 10 ? 2 : 0)}/s`
    case 'load':
      return trim(value, 2)
    case 'days':
      return `${trim(value, value < 10 ? 1 : 0)} d`
    default:
      return trim(value, Math.abs(value) < 10 && !Number.isInteger(value) ? 2 : 0)
  }
}

export function formatBytes(bytes: number): string {
  let v = Math.abs(bytes)
  let i = 0
  while (v >= 1024 && i < BYTE_UNITS.length - 1) {
    v /= 1024
    i++
  }
  const sign = bytes < 0 ? '-' : ''
  return `${sign}${trim(v, v < 10 ? 1 : 0)} ${BYTE_UNITS[i]}`
}

export function formatDuration(seconds: number): string {
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`
  if (seconds < 60) return `${trim(seconds, seconds < 10 ? 1 : 0)} s`
  const m = Math.floor(seconds / 60)
  if (m < 60) return `${m}m ${Math.round(seconds % 60)}s`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ${m % 60}m`
  const d = Math.floor(h / 24)
  return `${d}d ${h % 24}h`
}

/** Short relative time: "3m ago", "2h ago". */
export function timeAgo(iso?: string): string {
  if (!iso) return '—'
  const delta = (Date.now() - new Date(iso).getTime()) / 1000
  if (delta < 0) return 'just now'
  if (delta < 60) return `${Math.floor(delta)}s ago`
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`
  return `${Math.floor(delta / 86400)}d ago`
}

function trim(value: number, digits: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  })
}

/**
 * Axis tick formatting appropriate to the data actually being shown.
 *
 * <p>Driven by the spacing between points rather than by the requested range.
 * The two can disagree: a "month" of hourly buckets still needs the hour on the
 * label to be readable, and a range with only a few points recorded so far is
 * far denser than its name suggests. Passing the points lets the axis describe
 * the data instead of the request.
 */
export function timeTickFormatter(
  range: string,
  timestamps?: readonly string[],
): (iso: string) => string {
  const stepMs = medianStep(timestamps)

  // Fall back to the range when there is not enough data to measure a step.
  const effective = stepMs ?? rangeStepMs(range)

  return (iso: string) => {
    const d = new Date(iso)
    if (effective <= 2 * 60_000) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }
    if (effective <= 6 * 60 * 60_000) {
      // Sub-daily spacing: the clock time is the informative part. Include the
      // date too once the span is wide enough for days to be ambiguous.
      return spansMultipleDays(timestamps)
        ? d.toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
        : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    if (effective <= 24 * 60 * 60_000) {
      return d.toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit' })
    }
    return d.toLocaleDateString([], { day: '2-digit', month: 'short' })
  }
}

function rangeStepMs(range: string): number {
  switch (range) {
    case 'hour': return 30_000
    case 'day': return 5 * 60_000
    case 'week': return 60 * 60_000
    case 'month': return 60 * 60_000
    default: return 24 * 60 * 60_000
  }
}

/** Median gap between consecutive timestamps; robust to a single long outage. */
function medianStep(timestamps?: readonly string[]): number | null {
  if (!timestamps || timestamps.length < 3) return null
  const gaps: number[] = []
  for (let i = 1; i < timestamps.length; i++) {
    const gap = new Date(timestamps[i]).getTime() - new Date(timestamps[i - 1]).getTime()
    if (gap > 0) gaps.push(gap)
  }
  if (gaps.length === 0) return null
  gaps.sort((a, b) => a - b)
  return gaps[Math.floor(gaps.length / 2)]
}

function spansMultipleDays(timestamps?: readonly string[]): boolean {
  if (!timestamps || timestamps.length < 2) return false
  const first = new Date(timestamps[0])
  const last = new Date(timestamps[timestamps.length - 1])
  return first.toDateString() !== last.toDateString()
}

/** Human wording for the storage tier that answered a query. */
export const RESOLUTION_LABEL: Record<string, string> = {
  raw: 'full resolution',
  '5m': '5-minute averages',
  '1h': 'hourly averages',
  '1d': 'daily averages',
}
